import React, { useEffect, useState } from "react";
import { Grid } from "@mui/material";
import { Dialog } from "../../../Components/Dialogs/Dialog";
import {
  ControlledCustomSelect,
  OutlinedTextField,
} from "../../../Components/formComponents";
import { useFormik } from "formik";
import { IStoreState, useAppDispatch, useAppSelector } from "../../../redux";
import { OutlinedTimePicker } from "../../../Components/formComponents/CustomDatePicker";
import moment from "moment";
import {
  defaultWaitlistEntry,
  IWaitList,
  upsertWaitListAsync,
} from "../../../redux/waitlist";
import { LayoutTableSelector } from "../../Reservations/components/LayoutTableSelector/LayoutTableSelector";
import { LayoutSelector } from "../../Reservations/components/LayoutSelector/LayoutSelector";

export interface IUpsertWaitlistDialogProps {
  open: boolean;
  waitlist?: IWaitList;
  onClose: () => void;
  onSuccess?: (data: IWaitList) => void;
}

export const UpsertWaitlistDialog: React.FC<IUpsertWaitlistDialogProps> =
  React.memo((props) => {
    const { open, waitlist, onSuccess, onClose } = props;
    const dispatch = useAppDispatch();
    const { data: singleWaitList } = useAppSelector(
      (storeState: IStoreState) => storeState.waitlist.singleWaitList
    );
    const [tablesList, setTablesList] = useState<
      { label: string; value: string }[]
    >([]);

    const {
      values,
      errors,
      handleChange,
      handleSubmit,
      setValues,
      setFieldValue,
      isSubmitting,
      setSubmitting,
    } = useFormik({
      initialValues: defaultWaitlistEntry,
      validate: (values) => {
        const errors: any = {};
        if (!values.customer_name) {
          errors.customer_name = "*This field is required.";
        }
        if (!values.layout_uuid) {
          errors.layout_uuid = "*This field is required.";
        }
        if (!values.table_no) {
          errors.table_no = "*This field is required.";
        }
        if (!values.customer_mobile_no) {
          errors.customer_mobile_no = "*This field is required.";
        } else if (!/^\d{10}$/.test(values.customer_mobile_no)) {
          errors.customer_mobile_no = "*Mobile number must be 10 digits.";
        }
        if (!values.number_of_guests) {
          errors.number_of_guests = "*This field is required.";
        }
        return errors;
      },
      onSubmit: async (values) => {
        setSubmitting(true);
        dispatch(
          upsertWaitListAsync({
            ...values,
            waiting_time: values.waiting_time,
          })
        )
          .then((res) => {
            if (res.meta.requestStatus === "fulfilled") {
              onSuccess?.(values as IWaitList);
              onClose();
            }
          })
          .finally(() => {
            setSubmitting(false);
          });
      },
    });

    useEffect(() => {
      if (!waitlist) return;
      setValues(waitlist);
    }, [waitlist]);

    //   useEffect(() => {
    //     const newList = tablesLayout.tables
    //       .filter((table) => table.data.nodeType === "TABLE")
    //       .map((table) => ({
    //         label: table.data.table_number.toString(),
    //         value: table.data.uuid,
    //       }));
    //     setTablesList(newList);
    //   }, [tablesLayout]);
    const outletValue = React.useMemo(
      () =>
        values.outlet_uuid
          ? {
              outlet_uuid: values.outlet_uuid,
              outlet_name: values.outlet_name,
            }
          : null,
      [values.outlet_uuid, values.outlet_name]
    );

    const handleOutletSelect = React.useCallback(
      (outlet: any) => {
        setFieldValue("outlet_uuid", outlet.outlet_uuid);
        setFieldValue("outlet_name", outlet.outlet_name);
      },
      [setFieldValue]
    );

    return (
      <Dialog
        open={open}
        title="Add/Edit Waitlist Entry"
        size="sm"
        contentWrappedWithForm={{ onSubmit: handleSubmit }}
        onClose={onClose}
        actions={[
          {
            type: "button",
            variant: "contained",
            
            label: "Cancel",
            onClick: onClose,
          },
          {
            type: "submit",
            variant: "contained",
            disabled: isSubmitting,
            label: "Save",
          },
        ]}
      >
        <form>
          <Grid container spacing={2}>
            {/* <Grid item xs={12}>
              <OutletsAutoSearch
                value={outletValue}
                onSelect={handleOutletSelect}
                error={errors.outlet_uuid}
              />
            </Grid> */}

            <Grid item xs={12}>
              <LayoutSelector
                disabled={isSubmitting}
                value={values.layout_uuid}
                onChange={(newValue) => {
                  setValues({
                    ...values,
                    layout_uuid: newValue.value as string,
                    layout_name: newValue.label,
                  });
                }}
                error={errors.layout_uuid}
              />
            </Grid>
            <Grid item xs={12}>
              <LayoutTableSelector
                layout={values.layout_uuid}
                disabled={isSubmitting}
                value={values.table_uuid ? values.table_uuid : ""}
                onChange={(newValue) => {
                  setFieldValue("table_uuid", newValue.value as string);
                  setFieldValue("table_no", newValue.label);
                }}
                error={errors.table_no}
              />
            </Grid>
            <Grid item xs={12}>
              <OutlinedTextField
                label="Customer Name"
                id="customer_name"
                value={values.customer_name}
                onChange={handleChange}
                error={errors.customer_name}
              />
            </Grid>
            <Grid item xs={12}>
              <OutlinedTextField
                label="Mobile Number"
                id="customer_mobile_no"
                value={values.customer_mobile_no}
                onChange={handleChange}
                error={errors.customer_mobile_no}
              />
            </Grid>
            <Grid item xs={12}>
              <OutlinedTextField
                label="Email"
                id="customer_email"
                value={values.customer_email}
                onChange={handleChange}
                error={errors.customer_email}
              />
            </Grid>
            <Grid item xs={12}>
              <OutlinedTextField
                label="Number of Guests"
                id="number_of_guests"
                type="number"
                value={values.number_of_guests}
                onChange={handleChange}
                error={errors.number_of_guests}
              />
            </Grid>
            <Grid item xs={12}>
              <OutlinedTextField
                label="Estimated Waiting Time"
                id="waiting_time"
                type= "number"
                value={values.waiting_time}
                onChange={handleChange}
                error={errors.waiting_time}
              />
            </Grid>
            <Grid item xs={12}>
              <OutlinedTextField
                label="Priority"
                id="priority"
                type= "number"
                value={values.priority}
                onChange={handleChange}
                error={errors.priority}
              />
            </Grid>
            <Grid item xs={12}>
              <OutlinedTextField
                label="Notes"
                id="notes"
                multiline
                rows={3}
                value={values.notes}
                onChange={handleChange}
                error={errors.notes}
              />
            </Grid>
            <Grid item xs={12}>
              <ControlledCustomSelect
                label="Status"
                options={[
                  { label: "Active", value: "ACTIVE" },
                  { label: "Inactive", value: "INACTIVE" },
                ]}
                value={values.status || "ACTIVE"}
                onChange={(event) =>
                  setFieldValue("status", event.target.value)
                }
              />
            </Grid>
            
          </Grid>
        </form>
      </Dialog>
    );
  });
